import {
  Avatar,
  Box,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useContext, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { baseUrl } from "../../services/AzureStorage";
import Helper from "../../common/Helper";
import RouteConstants from "../../common/RouteConstants";
import { useNavigate } from "react-router-dom";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import GroupCombineStatsRadarChart from "./GroupCombineStatsRadarChart";
import { AppDataContext } from "../../common/appContext";
import HistoricalCombineStatsBarChart from "./HistoricalCombineStatsBarChart";

const BootstrapTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
  },
}));

const useStyles = makeStyles((theme) => ({
  root: {
    margin: "5px 15px",
    border: "1px solid lightgrey",
    borderRadius: "10px",
    boxShadow: "2px 2px 11px grey inset",
  },
  listItemIcon: {
    minWidth: "38px",
  },
  imageIcon: {
    width: "100%",
    height: "100%",
    cursor: "pointer",
  },
  iconRoot: {
    textAlign: "center",
    width: "100%",
    height: "50px",
  },
  cardStyle: {
    border: "1px solid lightgrey",
    borderRadius: "10px",
    height: "250px",
  },

  headerText: {
    marginBottom: theme.spacing(0.5),
    color: "rgb(29, 66, 138)",
  },
  headerText2: {
    marginBottom: theme.spacing(0.5),
    color: "rgb(29, 66, 138)",
  },
  headerTextInfo: {
    color: "rgb(29, 66, 138)",
    fontSize: "14px",
  },
  headerBox: {
    background: "#c1d0ed",
  },
  smBlue: {
    marginLeft: "16px",
    width: 26,
    height: 26,
    color: theme.palette.getContrastText("#0c2c75"),
    backgroundColor: "#1e3f7e",
    position: "relative",
    overflow: "hidden",
    borderRadius: "50%",
  },
  playerNameStyle: {
    cursor: "pointer",
    "&:hover": {
      color: "blue",
    },
  },
  dataField: {
    backgroundColor: "#1d428a",
    color: "white",
    width: "fit-content",
    padding: "3px",
    fontSize: "0.875rem",
    fontWeight: "400",
    textAlign: "center",
  },
  percentileField: {
    width: "fit-content",
    padding: "3px",
    fontSize: "0.875rem",
    fontWeight: "bold",
  },
  cellContent: {
    padding: "5px",
    textAlign: "center",
  },
  percentileCellContent: {
    display: "flex",
    justifyContent: "center",
  },
}));

const HistoricalPercentileAnthros = (props) => {
  const classes = useStyles();
  let navigate = useNavigate();
  const {
    anthros,
    eventId,
    anthroRadarData,
    loading,
    validateNumber,
    primaryPlayers,
    anthrosAVGRadarData,
    anthroGroupPercentile,
    isShowGroupPercentile,
    isShowDEXABodyFat,
  } = props;

  const {
    getWeightUnitValue,
    getLengthUnitValue,
    getHandDimensionsUnitValue,
    getHandDimensionsUnitLabelType,
    lengthUnits,
    getWeightUnitLabelType,
    weightUnits,
    getLengthUnitLabelType,
  } = useContext(AppDataContext);

  //#Destructuring Data
  const { primaryPlayer, secondaryPlayer, groupPercentile } = anthros;

  const {
    data: primaryPlayerData,
    avg: avgPrimary,
    percentile: primaryPercentile,
  } = primaryPlayer;

  const {
    data: secondaryPlayerData,
    avg: avgSecondary,
    percentile: secondaryPercentile,
  } = secondaryPlayer;

  //#Preaparing Percentile Radar Data
  const labels = anthroRadarData.map((val) => val.category);
  const compareGroup1Data = anthroRadarData.map((val) =>
    parseFloat(val.primaryPlayerPercentile).toFixed(2)
  );
  const compareGroup2Data = anthroRadarData.map((val) =>
    parseFloat(val.secondaryPlayerPercentile).toFixed(2)
  );

  //#Preaparing AVG Radar Data
  const avGLabels = anthrosAVGRadarData.map((val) => val.category);
  const aVGCompareGroup1Data = anthrosAVGRadarData.map((val) =>
    parseFloat(val.primaryPlayerAVG).toFixed(2)
  );
  const aVGCompareGroup2Data = anthrosAVGRadarData.map((val) =>
    parseFloat(val.groupAVG).toFixed(2)
  );

  //#Preaparing Group Percentile Radar Data
  const groupPercentileLabels = anthroGroupPercentile.map(
    (val) => val.category
  );
  const compareGroupPercentile1 = anthroGroupPercentile.map((val) =>
    parseFloat(val.primaryPlayerPercentile).toFixed(2)
  );
  const compareGroupPercentile2 = anthroGroupPercentile.map((val) =>
    parseFloat(val.secondaryPlayerPercentile).toFixed(2)
  );

  const viewPlayerProfile = (playerId) => {
    navigate(`/${eventId}${RouteConstants.VIEW_PLAYER_PROFILE}/${playerId}`, {
      state: { from: "CombineResults", tab: 0 },
    });
  };

  const historicalPercentileAnthrosColumn = [
    {
      id: "Player Name",
      headerName: "Player Name",
      style: { padding: "4px", paddingLeft: "25px" },
      unit: "",
    },
    {
      id: "Position",
      headerName: "Position",
      style: { padding: "4px", textAlign: "center" },
      unit: "",
    },
    {
      id: "College Name",
      headerName: "College Name",
      style: { padding: "4px", textAlign: "center" },
      unit: "",
    },
    {
      id: "Standing Reach",
      headerName: "Standing Reach",
      style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
      unit: `${getLengthUnitLabelType(lengthUnits)}`,
    },
    {
      id: "WingSpan Feet",
      headerName: "WingSpan Feet",
      style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
      unit: `${getLengthUnitLabelType(lengthUnits)}`,
    },
    // {
    //   id: "Height With Shoes",
    //   headerName: "Height With Shoes",
    //   style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
    //   unit: `${getLengthUnitLabelType(lengthUnits)}`,
    // },
    {
      id: "Height WithOut Shoes",
      headerName: "Height WithOut Shoes",
      style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
      unit: `${getLengthUnitLabelType(lengthUnits)}`,
    },
    {
      id: "Body Weight",
      headerName: "Body Weight",
      style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
      unit: `${getWeightUnitLabelType(weightUnits)}`,
    },
    {
      id: "Hand Length",
      headerName: "Hand Length",
      style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
      unit: `W${getHandDimensionsUnitLabelType(
        lengthUnits
      )} | L${getHandDimensionsUnitLabelType(lengthUnits)}`,
    },
    // {
    //   id: "Body Fat",
    //   headerName: "Body Fat",
    //   style: { padding: "4px", textAlign: "center" },
    //   unit: `(%)`,
    // },
    ...(isShowDEXABodyFat === "true"
      ? [
          {
            id: "DEXA Body Fat",
            headerName: "DEXA Body Fat",
            style: {
              padding: "4px",
              textAlign: "center",
              display: isShowDEXABodyFat === "true" ? "" : "none",
            },
            unit: `(%)`,
          },
        ]
      : []),
  ];

  const showAvgGroup = () =>
    (primaryPlayerData.length === 1 && secondaryPlayerData.length === 1) ||
    !primaryPlayers;

  return (
    <>
      <Container maxWidth={false}>
        <Box p={1}>
          {loading ? (
            <Box sx={{ textAlign: "center", minHeight: "50px" }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Paper elevation={3} sx={{ my: 2 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                  className={classes.headerBox}
                >
                  <Box display="flex" pl={2}>
                    <Typography className={`${classes.headerText} headerTitle`}>
                      Group 1
                    </Typography>
                  </Box>
                </Box>
                <Box p={2}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {historicalPercentileAnthrosColumn.map(
                            (col, index) => (
                              <BootstrapTooltip
                                title={col.id}
                                placement="top-start"
                                key={index}
                              >
                                <TableCell style={col.style}>
                                  {col.headerName}
                                </TableCell>
                              </BootstrapTooltip>
                            )
                          )}
                        </TableRow>
                      </TableHead>
                      <TableHead>
                        <TableRow>
                          {historicalPercentileAnthrosColumn.map(
                            (col, index) => (
                              <TableCell style={col.style} key={index}>
                                {col.unit}
                              </TableCell>
                            )
                          )}
                        </TableRow>
                      </TableHead>
                      {primaryPlayerData.length > 0 ? (
                        <>
                          <TableBody>
                            {/* Display Comparable Group 1 Details for Anthropometry */}
                            {primaryPlayerData?.map((val, index) => {
                              return (
                                <TableRow key={index}>
                                  <TableCell
                                    className={classes.stickyColumn}
                                    sx={{ padding: "4px" }}
                                  >
                                    <Box display="flex">
                                      {val.headshotURL &&
                                      val.headshotURL !== "" ? (
                                        <Avatar
                                          className={classes.smBlue}
                                          sx={{
                                            bgcolor: "#1d428a",
                                            cursor: "pointer",
                                          }}
                                          src={
                                            val.headshotURL
                                              ? baseUrl(val.headshotURL)
                                              : ""
                                          }
                                          alt="basketball"
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          <Typography variant="caption">
                                            {Helper.GetNameInitials(
                                              `${val.playerName}`
                                            )}
                                          </Typography>
                                        </Avatar>
                                      ) : (
                                        <Avatar
                                          className={classes.smBlue}
                                          sx={{
                                            bgcolor: "#1d428a",
                                            cursor: "pointer",
                                          }}
                                          alt="basketball"
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          <Typography variant="caption">
                                            {Helper.GetNameInitials(
                                              `${val.playerName}`
                                            )}
                                          </Typography>
                                        </Avatar>
                                      )}
                                      &nbsp;&nbsp;
                                      <BootstrapTooltip
                                        title="View Player Profile"
                                        placement="right-start"
                                      >
                                        <Typography
                                          variant="body2"
                                          className={classes.playerNameStyle}
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          {val.playerName}
                                        </Typography>
                                      </BootstrapTooltip>
                                    </Box>
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.position ? val.position : "-"}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.collegeOrClub
                                      ? val.collegeOrClub
                                      : "-"}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {getLengthUnitValue(
                                      validateNumber(val.standingReachFeet),
                                      validateNumber(val.standingReachInches)
                                    )}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {getLengthUnitValue(
                                      validateNumber(val.wingSpanFeet),
                                      validateNumber(val.wingSpanInches)
                                    )}
                                  </TableCell>

                                  {/* Note:hidden by client request */}
                                  {/* <TableCell className={classes.cellContent}>
                                    {getLengthUnitValue(
                                      validateNumber(val.heightWithShoesFeet),
                                      validateNumber(val.heightWithShoesInches)
                                    )}
                                  </TableCell> */}

                                  <TableCell className={classes.cellContent}>
                                    {getLengthUnitValue(
                                      validateNumber(
                                        val.heightWithOutShoesFeet
                                      ),
                                      validateNumber(
                                        val.heightWithOutShoesInches
                                      )
                                    )}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {getWeightUnitValue(
                                      validateNumber(val.bodyWeight)
                                    )}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {getHandDimensionsUnitValue(
                                      validateNumber(val.handWidth)
                                    )}
                                    |{" "}
                                    {getHandDimensionsUnitValue(
                                      validateNumber(val.handLength)
                                    )}
                                  </TableCell>
                                  {/* <TableCell className={classes.cellContent}>
                                    {val.bodyFat ? val.bodyFat : "-"}
                                  </TableCell> */}
                                  {isShowDEXABodyFat === "true" ? (
                                    <TableCell className={classes.cellContent}>
                                      {val.bodyFatDXA ? val.bodyFatDXA : "-"}
                                    </TableCell>
                                  ) : null}
                                </TableRow>
                              );
                            })}

                            {/* Display Comparable Group 1 Average Details for Anthropometry */}
                            {avgPrimary &&
                            Object.keys(avgPrimary).length > 0 ? (
                              <TableRow
                                sx={{
                                  backgroundColor: "#F5F5F5",
                                }}
                              >
                                <TableCell
                                  className={classes.stickyColumn}
                                  sx={{ padding: "4px" }}
                                ></TableCell>
                                <TableCell className={classes.cellContent}>
                                  {""}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Typography
                                    variant="subtitle2"
                                    display="block"
                                    gutterBottom
                                  >
                                    {"AVG"}
                                  </Typography>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {getLengthUnitValue(
                                    validateNumber(
                                      avgPrimary.standingReachFeet
                                    ),
                                    validateNumber(
                                      avgPrimary.standingReachInches
                                    )
                                  )}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {getLengthUnitValue(
                                    validateNumber(avgPrimary.wingSpanFeet),
                                    validateNumber(avgPrimary.wingSpanInches)
                                  )}
                                </TableCell>
                                {/* <TableCell className={classes.cellContent}>
                                  {getLengthUnitValue(
                                    validateNumber(
                                      avgPrimary.heightWithShoesFeet
                                    ),
                                    validateNumber(
                                      avgPrimary.heightWithShoesInches
                                    )
                                  )}
                                </TableCell> */}
                                <TableCell className={classes.cellContent}>
                                  {getLengthUnitValue(
                                    validateNumber(
                                      avgPrimary.heightWithOutShoesFeet
                                    ),
                                    validateNumber(
                                      avgPrimary.heightWithOutShoesInches
                                    )
                                  )}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {getWeightUnitValue(
                                    validateNumber(avgPrimary.bodyWeight)
                                  )}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {getHandDimensionsUnitValue(
                                    validateNumber(avgPrimary.handWidth)
                                  )}
                                  |{" "}
                                  {getHandDimensionsUnitValue(
                                    validateNumber(avgPrimary.handLength)
                                  )}
                                </TableCell>
                                {/* <TableCell className={classes.cellContent}>
                                  {avgPrimary.bodyFat
                                    ? avgPrimary.bodyFat?.toFixed(2)
                                    : "-"}
                                </TableCell> */}
                                <TableCell
                                  className={classes.cellContent}
                                  style={{
                                    display:
                                      isShowDEXABodyFat === "true"
                                        ? ""
                                        : "none",
                                  }}
                                >
                                  {avgPrimary.bodyFatDXA
                                    ? validateNumber(avgPrimary.bodyFatDXA)
                                    : "-"}
                                </TableCell>
                              </TableRow>
                            ) : (
                              ""
                            )}

                            {/* Display Comparable Group 1 Percentile Details for Anthropometry */}
                            {primaryPercentile &&
                            Object.keys(primaryPercentile).length > 0 ? (
                              <TableRow
                                sx={{
                                  backgroundColor: "#F5F5F5",
                                }}
                              >
                                <TableCell
                                  className={classes.stickyColumn}
                                  sx={{ padding: "4px" }}
                                ></TableCell>
                                <TableCell className={classes.cellContent}>
                                  {""}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Typography
                                    variant="subtitle2"
                                    display="block"
                                    gutterBottom
                                  >
                                    {"PERCENTILE"}
                                  </Typography>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {primaryPercentile.standingReachInches
                                        ? `${primaryPercentile.standingReachInches}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {primaryPercentile.wingSpanInches
                                        ? `${primaryPercentile.wingSpanInches}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>

                                {/* Note:hidden by client request */}
                                {/* <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {primaryPercentile.heightWithShoesInches
                                        ? `${primaryPercentile.heightWithShoesInches}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell> */}
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {primaryPercentile.heightWithOutShoesInches
                                        ? `${primaryPercentile.heightWithOutShoesInches}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {primaryPercentile.bodyWeight
                                        ? `${primaryPercentile.bodyWeight}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {primaryPercentile.handWidth
                                        ? `${primaryPercentile.handWidth}%`
                                        : `0%`}
                                      |{" "}
                                      {primaryPercentile.handLength
                                        ? `${primaryPercentile.handLength}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                {/* <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {primaryPercentile.bodyFat
                                        ? `${primaryPercentile.bodyFat}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell> */}
                                <TableCell
                                  className={classes.cellContent}
                                  style={{
                                    display:
                                      isShowDEXABodyFat === "true"
                                        ? ""
                                        : "none",
                                  }}
                                >
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {primaryPercentile.bodyFatDXA
                                        ? `${primaryPercentile.bodyFatDXA}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ) : (
                              ""
                            )}
                          </TableBody>
                        </>
                      ) : (
                        <>
                          <TableBody>
                            <TableRow>
                              <TableCell colSpan={10} align="center">
                                No Records Found
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </>
                      )}
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>

              <Paper elevation={3} sx={{ my: 2 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                  className={classes.headerBox}
                >
                  <Box display="flex" pl={2}>
                    <Typography className={`${classes.headerText} headerTitle`}>
                      Group 2
                    </Typography>
                  </Box>
                </Box>
                <Box p={2}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {historicalPercentileAnthrosColumn.map(
                            (col, index) => (
                              <BootstrapTooltip
                                title={col.id}
                                placement="top-start"
                                key={index}
                              >
                                <TableCell style={col.style}>
                                  {col.headerName}
                                </TableCell>
                              </BootstrapTooltip>
                            )
                          )}
                        </TableRow>
                      </TableHead>
                      <TableHead>
                        <TableRow>
                          {historicalPercentileAnthrosColumn.map(
                            (col, index) => (
                              <TableCell style={col.style} key={index}>
                                {col.unit}
                              </TableCell>
                            )
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {secondaryPlayerData.length > 0 ? (
                          <>
                            {/* Display Comparable Group 2 Details for Anthropometry */}
                            {secondaryPlayerData?.map((val, index) => {
                              return (
                                <TableRow key={index}>
                                  <TableCell
                                    className={classes.stickyColumn}
                                    sx={{ padding: "4px" }}
                                  >
                                    <Box display="flex">
                                      {val.headshotURL &&
                                      val.headshotURL !== "" ? (
                                        <Avatar
                                          className={classes.smBlue}
                                          sx={{
                                            bgcolor: "#1d428a",
                                            cursor: "pointer",
                                          }}
                                          src={
                                            val.headshotURL
                                              ? baseUrl(val.headshotURL)
                                              : ""
                                          }
                                          alt="basketball"
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          <Typography variant="caption">
                                            {Helper.GetNameInitials(
                                              `${val.playerName}`
                                            )}
                                          </Typography>
                                        </Avatar>
                                      ) : (
                                        <Avatar
                                          className={classes.smBlue}
                                          sx={{
                                            bgcolor: "#1d428a",
                                            cursor: "pointer",
                                          }}
                                          alt="basketball"
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          <Typography variant="caption">
                                            {Helper.GetNameInitials(
                                              `${val.playerName}`
                                            )}
                                          </Typography>
                                        </Avatar>
                                      )}
                                      &nbsp;&nbsp;
                                      <BootstrapTooltip
                                        title="View Player Profile"
                                        placement="right-start"
                                      >
                                        <Typography
                                          variant="body2"
                                          className={classes.playerNameStyle}
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          {val.playerName}
                                        </Typography>
                                      </BootstrapTooltip>
                                    </Box>
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.position ? val.position : "-"}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.collegeOrClub
                                      ? val.collegeOrClub
                                      : "-"}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {getLengthUnitValue(
                                      validateNumber(val.standingReachFeet),
                                      validateNumber(val.standingReachInches)
                                    )}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {getLengthUnitValue(
                                      validateNumber(val.wingSpanFeet),
                                      validateNumber(val.wingSpanInches)
                                    )}
                                  </TableCell>

                                  {/* Note:hidden by client request */}
                                  {/* <TableCell className={classes.cellContent}>
                                    {getLengthUnitValue(
                                      validateNumber(val.heightWithShoesFeet),
                                      validateNumber(val.heightWithShoesInches)
                                    )}
                                  </TableCell> */}
                                  <TableCell className={classes.cellContent}>
                                    {getLengthUnitValue(
                                      validateNumber(
                                        val.heightWithOutShoesFeet
                                      ),
                                      validateNumber(
                                        val.heightWithOutShoesInches
                                      )
                                    )}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {getWeightUnitValue(
                                      validateNumber(val.bodyWeight)
                                    )}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {getHandDimensionsUnitValue(
                                      validateNumber(val.handWidth)
                                    )}{" "}
                                    |{" "}
                                    {getHandDimensionsUnitValue(
                                      validateNumber(val.handLength)
                                    )}
                                  </TableCell>
                                  {/* <TableCell className={classes.cellContent}>
                                    {val.bodyFat
                                      ? validateNumber(val.bodyFat)
                                      : "-"}
                                  </TableCell> */}
                                  <TableCell
                                    className={classes.cellContent}
                                    style={{
                                      display:
                                        isShowDEXABodyFat === "true"
                                          ? ""
                                          : "none",
                                    }}
                                  >
                                    {val.bodyFatDXA
                                      ? validateNumber(val.bodyFatDXA)
                                      : "-"}
                                  </TableCell>
                                </TableRow>
                              );
                            })}

                            {/* Display Comparable Group 1 Average Details for Anthropometry */}
                            {avgSecondary &&
                            Object.keys(avgSecondary).length > 0 ? (
                              <TableRow
                                sx={{
                                  backgroundColor: "#F5F5F5",
                                }}
                              >
                                <TableCell
                                  className={classes.stickyColumn}
                                  sx={{ padding: "4px" }}
                                ></TableCell>
                                <TableCell className={classes.cellContent}>
                                  {""}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Typography
                                    variant="subtitle2"
                                    display="block"
                                    gutterBottom
                                  >
                                    {"AVG"}
                                  </Typography>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {getLengthUnitValue(
                                    validateNumber(
                                      avgSecondary.standingReachFeet
                                    ),
                                    validateNumber(
                                      avgSecondary.standingReachInches
                                    )
                                  )}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {getLengthUnitValue(
                                    validateNumber(avgSecondary.wingSpanFeet),
                                    validateNumber(avgSecondary.wingSpanInches)
                                  )}
                                </TableCell>

                                {/* Note:hidden by client */}
                                {/* <TableCell className={classes.cellContent}>
                                  {getLengthUnitValue(
                                    validateNumber(
                                      avgSecondary.heightWithShoesFeet
                                    ),
                                    validateNumber(
                                      avgSecondary.heightWithShoesInches
                                    )
                                  )}
                                </TableCell> */}
                                <TableCell className={classes.cellContent}>
                                  {getLengthUnitValue(
                                    validateNumber(
                                      avgSecondary.heightWithOutShoesFeet
                                    ),
                                    validateNumber(
                                      avgSecondary.heightWithOutShoesInches
                                    )
                                  )}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {getWeightUnitValue(
                                    validateNumber(avgSecondary.bodyWeight)
                                  )}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {getHandDimensionsUnitValue(
                                    validateNumber(avgSecondary.handWidth)
                                  )}
                                  |{" "}
                                  {getHandDimensionsUnitValue(
                                    validateNumber(avgSecondary.handLength)
                                  )}
                                </TableCell>
                                {/* <TableCell className={classes.cellContent}>
                                  {avgSecondary.bodyFat
                                    ? avgSecondary.bodyFat?.toFixed(2)
                                    : "-"}
                                </TableCell> */}
                                <TableCell
                                  className={classes.cellContent}
                                  style={{
                                    display:
                                      isShowDEXABodyFat === "true"
                                        ? ""
                                        : "none",
                                  }}
                                >
                                  {avgSecondary.bodyFatDXA
                                    ? validateNumber(avgSecondary.bodyFatDXA)
                                    : "-"}
                                </TableCell>
                              </TableRow>
                            ) : (
                              ""
                            )}

                            {/* Display Comparable Group 1 Percentile Details for Anthropometry */}
                            {secondaryPercentile &&
                            Object.keys(secondaryPercentile).length > 0 ? (
                              <TableRow
                                sx={{
                                  backgroundColor: "#F5F5F5",
                                }}
                              >
                                <TableCell
                                  className={classes.stickyColumn}
                                  sx={{ padding: "4px" }}
                                ></TableCell>
                                <TableCell className={classes.cellContent}>
                                  {""}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Typography
                                    variant="subtitle2"
                                    display="block"
                                    gutterBottom
                                  >
                                    {"PERCENTILE"}
                                  </Typography>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {secondaryPercentile.standingReachInches
                                        ? `${secondaryPercentile.standingReachInches}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {secondaryPercentile.wingSpanInches
                                        ? `${secondaryPercentile.wingSpanInches}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>

                                {/* Note:hidden by client*/}
                                {/* <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {secondaryPercentile.heightWithShoesInches
                                        ? `${secondaryPercentile.heightWithShoesInches}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell> */}
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {secondaryPercentile.heightWithOutShoesInches
                                        ? `${secondaryPercentile.heightWithOutShoesInches}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {secondaryPercentile.bodyWeight
                                        ? `${secondaryPercentile.bodyWeight}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {secondaryPercentile.handWidth
                                        ? `${secondaryPercentile.handWidth}%`
                                        : `0%`}
                                      |{" "}
                                      {secondaryPercentile.handLength
                                        ? `${secondaryPercentile.handLength}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                {/* <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {secondaryPercentile.bodyFat
                                        ? `${secondaryPercentile.bodyFat}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell> */}
                                <TableCell
                                  className={classes.cellContent}
                                  style={{
                                    display:
                                      isShowDEXABodyFat === "true"
                                        ? ""
                                        : "none",
                                  }}
                                >
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {secondaryPercentile.bodyFatDXA
                                        ? `${secondaryPercentile.bodyFatDXA}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ) : (
                              ""
                            )}
                          </>
                        ) : (
                          <TableRow>
                            <TableCell colSpan={10} align="center">
                              No Records Found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>

              {/* GROUP PERCENTILE SECTION */}
              {isShowGroupPercentile === "true" ? (
                <>
                  <Paper elevation={3} sx={{ my: 2 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      flexDirection="row"
                      className={classes.headerBox}
                    >
                      <Box display="flex" pl={2}>
                        <Typography
                          className={`${classes.headerText} headerTitle`}
                        >
                          Group Percentile
                        </Typography>
                      </Box>
                    </Box>
                    <Box p={2}>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              {historicalPercentileAnthrosColumn.map(
                                (col, index) => (
                                  <BootstrapTooltip
                                    title={col.id}
                                    placement="top-start"
                                    key={index}
                                  >
                                    <TableCell style={col.style}>
                                      {col.headerName}
                                    </TableCell>
                                  </BootstrapTooltip>
                                )
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {/* Display Group Percentile Details for Anthropometry */}
                            {groupPercentile &&
                            Object.keys(groupPercentile).length > 0 ? (
                              <TableRow>
                                <TableCell
                                  className={classes.stickyColumn}
                                  sx={{ padding: "4px" }}
                                ></TableCell>
                                <TableCell className={classes.cellContent}>
                                  {""}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Typography
                                    variant="subtitle2"
                                    display="block"
                                    gutterBottom
                                  >
                                    {""}
                                  </Typography>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.standingReachInches
                                        ? `${groupPercentile.standingReachInches}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.wingSpanInches
                                        ? `${groupPercentile.wingSpanInches}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>

                                {/* Note:hidden by client */}
                                {/* <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.heightWithShoesInches
                                        ? `${groupPercentile.heightWithShoesInches}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell> */}
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.heightWithOutShoesInches
                                        ? `${groupPercentile.heightWithOutShoesInches}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.bodyWeight
                                        ? `${groupPercentile.bodyWeight}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.handWidth
                                        ? `${groupPercentile.handWidth}%`
                                        : `0%`}
                                      |{" "}
                                      {groupPercentile.handLength
                                        ? `${groupPercentile.handLength}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.bodyFat
                                        ? `${groupPercentile.bodyFat}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell
                                  className={classes.cellContent}
                                  style={{
                                    display:
                                      isShowDEXABodyFat === "true"
                                        ? ""
                                        : "none",
                                  }}
                                >
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.bodyFatDXA
                                        ? `${groupPercentile.bodyFatDXA}%`
                                        : `0%`}
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ) : (
                              ""
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Paper>
                </>
              ) : null}

              <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ my: 2 }}
              >
                {/* <Grid item md={6} sm={12} xs={12} justifyContent="center">
                  {anthrosAVGRadarData.length > 0 ? (
                    <GroupCombineStatsRadarChart
                      labels={avGLabels}
                      data={aVGCompareGroup1Data}
                      name={"Group 1"}
                      otherPlayerdata={aVGCompareGroup2Data}
                      nameotherplayer={"Group 2}
                      chartName={"Historical Group Average "}
                      isPercentileChart={false}
                      max={300}
                    />
                  ) : (
                    ""
                  )}
                </Grid> */}

                <Grid item md={6} sm={12} xs={12} justifyContent="center">
                  {anthroRadarData.length > 0 ? (
                    <HistoricalCombineStatsBarChart
                      labels={labels}
                      priamryPlayerData={compareGroup1Data}
                      primaryPlayerName={"Group 1"}
                      otherPlayerdata={compareGroup2Data}
                      nameOtherPlayer={"Group 2"}
                      chartName={"Historical Group Percentile"}
                      max={100}
                    />
                  ) : (
                    ""
                  )}
                </Grid>
              </Grid>

              {isShowGroupPercentile === "true" ? (
                <>
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ my: 2 }}
                  >
                    <Grid item md={6} sm={12} xs={12} justifyContent="center">
                      {anthroGroupPercentile.length > 0 ? (
                        <HistoricalCombineStatsBarChart
                          labels={groupPercentileLabels}
                          priamryPlayerData={compareGroupPercentile1}
                          primaryPlayerName={"Group 1"}
                          otherPlayerdata={compareGroupPercentile2}
                          nameOtherPlayer={"Group Percentile"}
                          chartName={"Historical Combined Group Percentile"}
                          max={100}
                        />
                      ) : (
                        ""
                      )}
                    </Grid>
                  </Grid>
                </>
              ) : null}
            </>
          )}
        </Box>
      </Container>
    </>
  );
};

export default HistoricalPercentileAnthros;
